/**
 * Les liaisons de champs (bindings) sur le canvas fabric — la logique partagée entre les
 * DEUX éditeurs (images : fabric_controller.ts, vidéo : composition_editor/fabric_controller)
 * pour la sous-page Variables de la toolbox (plan media-template-generators §9).
 *
 * Ici : la mutation de l'objet sélectionné, et rien d'autre. Le rituel d'après-mutation
 * appartient à chaque contrôleur (export des layers + interpolation d'affichage côté
 * images, resynchronisation des overlays côté vidéo) — chaque fonction rend `true` si elle
 * a mutié, à charge de l'appelant de dérouler son rituel.
 *
 * Le champ lié stocke L'EXPRESSION : ce que le texte rend dépend de qui regarde (l'affilié,
 * pas l'admin qui a lié) — la résolution appartient à l'affichage (interpolateBoundFields,
 * pour les éditeurs qui la font) et, pour de vrai, à la divergence chez l'affilié
 * (Composition#resolve_with!).
 *
 * Un texte lié s'édite aussi dans sa textbox (plan bound-text-styles §3.4) : elle garde les
 * valeurs, ses variables sont atomiques, et l'expression du binding suit la frappe
 * (attachBoundTextEditing).
 */
import {
  setTextContent, setGhost, isGhost, setEclipsed, isEclipsed, setDisplayFill, layoutParents, layoutDescendants,
  type FabricEditor,
} from "@frabr/fabric-editor";
import { util, type FabricObject } from "fabric";
import { evaluateCondition } from "./condition";
import { bindingScope } from "./variable_chain";
import { looksLikeColor } from "./fill_cases";
import { invertSegments, mapIndexBack, transposeStyles, type StyleRange } from "./style_transposition";
import {
  hasVariable, interpolateExpression, interpolateWithMap, liveExpression, resolvedSource,
  type BindingSpec, type Interpolation, type Segment,
} from "./variable_interpolation";

interface BindDetail {
  field?: string;
  expr?: string;
  scope?: string;
  /** Le reste voyage avec le binding : la provenance d'éditeur (les `cases` d'un fill). */
  [key: string]: unknown;
}

/** Lie un champ de l'objet sélectionné à une expression. */
export function bindSelectedField(editor: FabricEditor | null, detail: BindDetail = {}): boolean {
  const obj = editor?.selection?.current as any;
  const { field = "text", expr, scope, ...rest } = detail;
  if (!obj || !expr) return false;

  obj.set("bindings", { ...(obj.get("bindings") || {}), [field]: { expr, scope, ...rest } });
  if (field === "text") {
    // Le panneau remplace le texte en bloc : les styles par caractère, indexés sur
    // l'ancienne chaîne, ne valent plus rien (plan bound-text-styles §4)
    obj.styles = {};
    dropMap(obj);
    // setTextContent remesure le calque et relance la mise en page — un texte lié qui
    // grandit doit faire grandir son conteneur (hug).
    setTextContent(obj, expr);
  }
  editor!.layout.relayout();
  return true;
}

/** Délie un champ : l'expression disparaît, la valeur en place redevient éditable. */
export function unbindSelectedField(editor: FabricEditor | null, field = "text"): boolean {
  const obj = editor?.selection?.current as any;
  if (!obj) return false;

  const bindings = { ...(obj.get("bindings") || {}) };
  delete bindings[field];
  obj.set("bindings", bindings);
  // La valeur affichée reste, avec ses styles tels qu'ils se voient : plus de carte ni de
  // tokens
  if (field === "text") dropMap(obj);
  editor!.layout.relayout();
  return true;
}

/** Pose le texte de l'objet sélectionné (sous-page Variables : une expression sans token
 *  n'est pas une liaison, c'est du texte). */
export function setSelectedText(editor: FabricEditor | null, text: string | undefined): boolean {
  const obj = editor?.selection?.current as any;
  if (!obj || !("text" in obj)) return false;

  setTextContent(obj, text ?? "");
  editor!.layout.relayout();
  return true;
}

export interface DynamicMedia {
  field: string;
  expr: string;
  scope: string;
}

/** Une carte de l'onglet Médias dynamiques, posée ou lâchée : son binding rend le calque
 *  dynamique — l'expansion/le relais poseront le fichier réel. Rend le calque lié. */
export function applyDynamicBinding(
  editor: FabricEditor | null,
  obj: FabricObject | null | undefined,
  dynamic?: DynamicMedia,
): FabricObject | null {
  if (!editor || !obj || !dynamic) return null;

  obj.set("bindings", {
    ...(obj.get("bindings") || {}),
    [dynamic.field]: { expr: dynamic.expr, scope: dynamic.scope },
  });
  editor.canvas.requestRenderAll();
  return obj;
}

/** Affiche les champs liés — textes, images, couleur selon l'annonce, conditions de
 *  visibilité — interpolés AVEC LES VALEURS DU CONTEXTE, sans toucher au document : le canvas
 *  montre ce que ce lecteur-ci verra, le fichier garde ses tokens.
 *  Un binding `resolved` (sujet consommé à l'expansion) garde sa valeur en place.
 *  Un texte se remplace sur place ; une image liée (média dynamique) charge la source du
 *  lecteur dans le cadre, l'exemple stocké mis de côté dans le binding (`sample`) pour que
 *  l'export le remette — le nom reste, l'image est un champ lié comme un autre. */
export function interpolateBoundFields(editor: FabricEditor | null, values: Record<string, unknown>): void {
  if (!editor) return;

  editor.layers.all.forEach((obj: any) => {
    const bindings = obj.get("bindings") || {};
    const text = liveExpression(bindings.text);
    if (text !== null) showBoundText(obj, text, values);

    const image = liveExpression(bindings["image.src"]);
    if (image !== null) void showBoundImage(editor, obj, image, values);

    applyBoundStyle(obj, "fill", values);
  });
  applyConditions(editor, values);
  editor.layout.relayout();
}

/** Le texte lié affiché pour ces valeurs, ses styles par caractère transposés avec lui
 *  (plan bound-text-styles §3.3). Les styles de l'objet suivent son texte courant : s'il
 *  affichait déjà des valeurs (carte en place), ils reviennent d'abord dans l'espace de
 *  l'expression par la carte inversée. La nouvelle carte reste sur l'objet, pour l'export. */
function showBoundText(obj: any, expr: string, values: Record<string, unknown>): void {
  // En édition, on ne remplace pas le texte sous la frappe
  if (obj.isEditing) return;

  const current = util.stylesToArray(obj.styles ?? {}, obj.text ?? "") as StyleRange[];
  const previous = obj.bindingSegments as Segment[] | undefined;
  const inExpression = previous ? transposeStyles(current, invertSegments(previous)) : current;

  const map = interpolateWithMap(expr, values, { keepUnknown: true });
  setTextContent(obj, map.text);
  obj.styles = util.stylesFromArray(transposeStyles(inExpression, map.segments), map.text);
  obj.boundValues = values;
  keepMap(obj, map);
}

/**
 * La carte d'un texte lié affiché, gardée sur l'objet : `bindingSegments` (index plats, pour
 * les styles — sérialisé le temps d'un snapshot, retiré à l'export), `boundUnits` (offsets de
 * chaîne, pour reporter une frappe) et `tokenSpans` (les variables affichées, atomiques).
 */
function keepMap(obj: any, { segments, units }: Interpolation): void {
  obj.bindingSegments = segments;
  obj.boundUnits = units;
  obj.tokenSpans = segments.filter((segment) => segment.kind !== "text").map((segment) => segment.to);
}

/* ---------- l'édition d'un texte lié dans sa textbox ---------- */

/**
 * Branche l'édition des textes liés sur le canvas (plan bound-text-styles §3.4) : la textbox
 * garde les valeurs affichées ; chaque frappe est reportée dans l'expression du binding à
 * travers la carte (une variable est atomique, une frappe ne touche que les parties fixes ou
 * des variables entières) ; à la sortie, une expression sans variable délie, et l'hôte
 * ré-affiche (`onChanged` : son rituel d'après-mutation). Rend de quoi débrancher.
 */
export function attachBoundTextEditing(editor: FabricEditor, onChanged: () => void): () => void {
  const canvas = editor.canvas;
  const entered = (e: { target?: any }) => {
    if (e.target && isBoundText(e.target)) e.target.boundTextBefore = e.target.text;
  };
  const changed = (e: { target?: any }) => {
    if (e.target?.boundTextBefore !== undefined && isBoundText(e.target)) followEdit(e.target);
  };
  const exited = (e: { target?: any }) => {
    const obj = e.target;
    if (obj?.boundTextBefore === undefined) return;
    delete obj.boundTextBefore;
    settleExpression(obj);
    onChanged();
  };
  canvas.on("text:editing:entered" as never, entered as never);
  canvas.on("text:changed" as never, changed as never);
  canvas.on("text:editing:exited" as never, exited as never);
  return () => {
    canvas.off("text:editing:entered" as never, entered as never);
    canvas.off("text:changed" as never, changed as never);
    canvas.off("text:editing:exited" as never, exited as never);
  };
}

function isBoundText(obj: any): boolean {
  return liveExpression(obj.get?.("bindings")?.text) !== null;
}

/**
 * Une frappe, reportée dans l'expression. L'édition est lue comme un seul remplacement :
 * ancien texte = avant + retiré + après, nouveau = avant + inséré + après, le curseur au bout
 * de l'insertion. Ses bornes passent dans l'espace de l'expression par la carte (une borne
 * dans une valeur va à la borne de sa variable), puis l'expression est ré-interpolée pour
 * reprendre la carte et les variables.
 */
function followEdit(obj: any): void {
  const before: string = obj.boundTextBefore;
  const after: string = obj.text;
  obj.boundTextBefore = after;
  if (before === after) return;

  const bindings = obj.get("bindings") || {};
  const expr: string = bindings.text.expr;
  const values = obj.boundValues ?? {};
  const units: Segment[] = obj.boundUnits ?? interpolateWithMap(expr, values, { keepUnknown: true }).units;

  const cursor = Array.isArray(obj._text) ? obj._text.slice(0, obj.selectionEnd).join("").length : after.length;
  const [from, to, inserted] = replacedRange(before, after, cursor);
  const head = mapIndexBack(units, from, "start");
  const tail = Math.max(head, mapIndexBack(units, to, "end"));
  const next = expr.slice(0, head) + inserted + expr.slice(tail);

  obj.set("bindings", { ...bindings, text: { ...bindings.text, expr: next } });
  keepMap(obj, interpolateWithMap(next, values, { keepUnknown: true }));
}

/** Le remplacement qui mène de `before` à `after`, le curseur (offset dans `after`) au bout
 *  de l'insertion : [début, fin) dans `before`, et le texte inséré. */
export function replacedRange(before: string, after: string, cursor: number): [number, number, string] {
  const end = before.length - (after.length - cursor);
  let start = 0;
  const limit = Math.min(cursor, Math.max(end, 0));
  while (start < limit && before[start] === after[start]) start++;
  return [start, Math.max(start, end), after.slice(start, cursor)];
}

/* ---------- insérer une variable (le panneau Variables) ---------- */

/** Une variable écrite à la place de [from, to) d'une expression : le résultat et le curseur,
 *  juste derrière elle. */
export function insertedToken(expr: string, token: string, from: number, to = from): { expr: string; caret: number } {
  const head = expr.slice(0, Math.min(from, to));
  const next = head + token + expr.slice(Math.max(from, to));
  return { expr: next, caret: head.length + token.length };
}

/** Sans curseur connu : la variable se colle à la fin, précédée d'une espace si
 *  l'expression n'en finit pas par une. */
export function appendedToken(expr: string, token: string): { expr: string; caret: number } {
  const spaced = expr.length > 0 && !/\s$/.test(expr);
  return insertedToken(expr, (spaced ? " " : "") + token, expr.length);
}

export interface InsertTokenDetail {
  token?: string;
  values?: Record<string, unknown>;
  subjectTokens?: string[];
  /** Rendu au panneau : l'expression après insertion, quand la textbox avait la main. */
  expr?: string;
}

/**
 * Une variable insérée au curseur du texte EN ÉDITION sur la slide — le panneau Variables
 * demande, la textbox a la main. L'expression reçoit le token là où est le curseur (converti
 * par la carte pour un texte déjà lié ; une variable sélectionnée est remplacée), la textbox
 * affiche la valeur et place son curseur derrière ; un texte qui n'était pas lié le devient.
 * Rend la nouvelle expression, null si aucun texte n'est en édition (le panneau insère alors
 * chez lui). Comme une expression reposée depuis le panneau, les styles par caractère
 * s'effacent.
 */
export function insertTokenAtCaret(editor: FabricEditor | null, detail: InsertTokenDetail = {}): string | null {
  const obj = editor?.selection?.current as any;
  const { token, values = {}, subjectTokens = [] } = detail;
  if (!token || !obj?.isEditing || !("text" in obj)) return null;

  const bindings = obj.get("bindings") || {};
  const bound = liveExpression(bindings.text) !== null;
  const expr: string = bound ? bindings.text.expr : obj.text;
  const offset = (index: number) => obj._text.slice(0, index).join("").length;
  let from = offset(obj.selectionStart);
  let to = offset(obj.selectionEnd);
  if (bound) {
    const units: Segment[] = obj.boundUnits ?? interpolateWithMap(expr, values, { keepUnknown: true }).units;
    from = mapIndexBack(units, from, "start");
    to = Math.max(from, mapIndexBack(units, to, "end"));
  }
  const { expr: next } = insertedToken(expr, token, from, to);
  obj.set("bindings", { ...bindings, text: { ...bindings.text, expr: next, scope: bindingScope(next, subjectTokens) } });
  obj.styles = {};

  const map = interpolateWithMap(next, values, { keepUnknown: true });
  // Posé AVANT le texte : la frappe suivante repart de ce texte, pas de l'ancien.
  obj.boundTextBefore = map.text;
  obj.boundValues = values;
  setTextContent(obj, map.text);
  keepMap(obj, map);
  const inserted = map.units.find((segment) => segment.kind !== "text" && segment.from[0] === from);
  const caret = inserted ? inserted.to[1] : from + token.length;
  obj.selectionStart = obj.selectionEnd = graphemeIndex(obj._text, caret);
  obj._updateTextarea?.();
  editor!.layout.relayout();
  return next;
}

/** L'index de `_text` (graphèmes) qui correspond à un offset de chaîne. */
function graphemeIndex(graphemes: string[], offset: number): number {
  let index = 0;
  let at = 0;
  while (index < graphemes.length && at < offset) at += graphemes[index++].length;
  return index;
}

/** Sortie d'édition : plus de variable, plus de liaison — c'est du texte (règle du panneau). */
function settleExpression(obj: any): void {
  const { text: spec, ...rest } = obj.get("bindings") || {};
  if (hasVariable(spec?.expr ?? "")) return;

  obj.set("bindings", rest);
  dropMap(obj);
}

/** Un texte qui n'est plus lié n'a plus de carte. */
function dropMap(obj: any): void {
  delete obj.bindingSegments;
  delete obj.boundUnits;
  delete obj.tokenSpans;
}

/** Un style lié (`fill` : la couleur selon l'annonce) : sa valeur pour ces valeurs est une
 *  couleur d'AFFICHAGE (lib displayStyle), posée le temps du rendu — `fill` reste la couleur
 *  de l'auteur, le « sinon », celle que le document exporte. Sans binding, ou pour une
 *  expression qui ne rend pas une couleur, l'objet se dessine avec la sienne. */
function applyBoundStyle(obj: any, field: string, values: Record<string, unknown>): void {
  const expr = liveExpression((obj.get("bindings") || {})[field]);
  const value = expr === null ? "" : interpolateExpression(expr, values);
  setDisplayFill(obj, looksLikeColor(value) ? value : null);
}

/** Le champ de la condition de visibilité d'un calque (plan media-template-generators §4). */
export const CONDITION_FIELD = "visible";

/** La condition en attente d'un calque, null sans condition. */
export function conditionOf(obj: FabricObject): string | null {
  return liveExpression(((obj.get("bindings") as Record<string, BindingSpec> | undefined) ?? {})[CONDITION_FIELD]);
}

/** Ce que la barre d'actions montre de la condition d'un calque : affiché ou masqué pour le
 *  sujet d'aperçu, rien sans condition (un descendant masqué par son parent est `hidden` aussi). */
export function conditionStateOf(obj: FabricObject): "shown" | "hidden" | undefined {
  if (isGhost(obj) || isEclipsed(obj)) return "hidden";
  return conditionOf(obj) === null ? undefined : "shown";
}

/** Un calque masqué par sa condition pour les valeurs du contexte — la racine d'une branche
 *  masquée (ses descendants de mise en page suivent son sort). */
export interface HiddenLayer {
  obj: FabricObject;
  layerId: string;
  condition: string;
  revealed: boolean;
}

const revealedByEditor = new WeakMap<FabricEditor, Set<string>>();
const hiddenByEditor = new WeakMap<FabricEditor, HiddenLayer[]>();

/** Les calques masqués de la dernière évaluation (applyConditions), pour leurs pastilles. */
export function hiddenLayersOf(editor: FabricEditor | null): HiddenLayer[] {
  return (editor && hiddenByEditor.get(editor)) || [];
}

/** Révèle (fantôme à 22 %, dans le flux, réglable) ou rééclipse un calque masqué. L'hôte
 *  rejoue ensuite les conditions (interpolateBoundFields). */
export function revealHiddenLayer(editor: FabricEditor | null, layerId: string, revealed: boolean): void {
  if (!editor) return;
  const set = revealedByEditor.get(editor) ?? new Set<string>();
  if (revealed) set.add(layerId);
  else set.delete(layerId);
  revealedByEditor.set(editor, set);
}

/** Les conditions de visibilité, évaluées AVEC LES VALEURS DU CONTEXTE, sans toucher au
 *  document. Un calque masqué pour ce sujet est ÉCLIPSÉ (lib : ni dessiné, ni atteignable, hors
 *  du flux — le canvas montre ce que le rendu montrera), ses descendants de mise en page avec
 *  lui ; l'hôte le signale par une pastille. Révélé à la demande (revealHiddenLayer), il
 *  redevient un fantôme à 22 %, dans le flux, réglable. Une condition illisible masque, comme
 *  au serveur. Le relayout est à l'appelant (le flux a pu changer). */
export function applyConditions(editor: FabricEditor | null, values: Record<string, unknown>): void {
  if (!editor) return;

  const all = editor.layers.all as FabricObject[];
  const roots = new Map<string, { obj: FabricObject; condition: string }>();
  all.forEach((obj) => {
    const code = conditionOf(obj);
    if (code !== null && !evaluateCondition(code, values)) roots.set(String(obj.get("layerId")), { obj, condition: code });
  });
  const parents = layoutParents(all as never);
  const revealed = revealedByEditor.get(editor) ?? new Set<string>();
  const shownRoots = [...roots.keys()].filter((id) => revealed.has(id));
  const ghosts = new Set([...shownRoots, ...layoutDescendants(parents, shownRoots)]);
  const hidden = new Set([...roots.keys(), ...layoutDescendants(parents, roots.keys())]);

  let changed = false;
  all.forEach((obj) => {
    const id = String(obj.get("layerId"));
    const ghost = ghosts.has(id);
    const eclipsed = hidden.has(id) && !ghost;
    if (setGhost(obj, ghost)) changed = true;
    if (setEclipsed(obj, eclipsed)) changed = true;
    if (eclipsed && editor.canvas.getActiveObject() === obj) editor.canvas.discardActiveObject();
  });
  hiddenByEditor.set(editor, [...roots].map(([layerId, { obj, condition }]) =>
    ({ obj, layerId, condition, revealed: revealed.has(layerId) })));
  if (changed) editor.canvas.requestRenderAll();
}

/** Le cadre montre la source de CE lecteur (asynchrone : un chargement d'image). Sans
 *  valeur complète, ou si l'image ne charge pas, l'exemple reste. Jamais de sélection :
 *  c'est un affichage, pas un geste. */
async function showBoundImage(
  editor: FabricEditor,
  obj: any,
  expr: string,
  values: Record<string, unknown>,
): Promise<void> {
  if (typeof obj.imageSrc !== "string") return; // une forme-image seulement
  const src = resolvedSource(expr, values);
  if (!src || src === obj.imageSrc) return;

  const bindings = obj.get("bindings") || {};
  const spec: BindingSpec = bindings["image.src"];
  if (spec.sample === undefined) {
    obj.set("bindings", { ...bindings, "image.src": { ...spec, sample: obj.toObject().image } });
  }
  try {
    await editor.layers.replaceImageSource(obj, src, { select: false });
    editor.canvas.requestRenderAll();
  } catch {
    // l'image du lecteur ne charge pas : l'exemple reste
  }
}

/** Ce qu'on EXPORTE est le document, pas son rendu chez ce lecteur : un champ lié EN
 *  ATTENTE ressort avec son TOKEN, quelle que soit la valeur affichée au canvas. Un
 *  binding `resolved` exporte sa VALEUR — ré-exporter l'expression écraserait le sujet
 *  consommé à l'expansion (« Marrakech » redeviendrait « $VILLE » au premier save).
 *  Une image liée ressort avec son EXEMPLE (source et identité du média), mis de côté
 *  dans le binding le temps de l'affichage et retiré à l'export : le document ne porte
 *  jamais la photo d'un lecteur. */
export function withExpressions<T>(layers: T[]): T[] {
  return layers.map((layer: any) => {
    let out = layer;
    const text = liveExpression(layer?.bindings?.text);
    if (text !== null) out = { ...out, text };
    // Les styles par caractère reviennent dans l'espace de l'expression par la carte
    // inversée (plan bound-text-styles §3.3) ; la carte ne sort jamais du canvas
    if (Array.isArray(layer?.bindingSegments)) {
      const { bindingSegments, ...rest } = out;
      out = rest;
      if (text !== null && Array.isArray(layer.styles)) {
        out = { ...out, styles: transposeStyles(layer.styles, invertSegments(bindingSegments)) };
      }
    }

    const image: BindingSpec | undefined = layer?.bindings?.["image.src"];
    if (image?.sample !== undefined) {
      const { sample, ...spec } = image;
      out = { ...out, image: sample, bindings: { ...out.bindings, "image.src": spec } };
    }
    return out;
  });
}

// L'inverse pour des layers SÉRIALISÉS (rendus d'affichage : preview Remotion, vignettes)
// existe déjà : interpolateLayers (lib variable_interpolation).
